import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark, faTriangleExclamation, faSpinner } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import MultiCreateTable from '@/components/admin/recommendations/MultiCreateTable.tsx';
import { handleAPIError } from '@/utils/toasts.ts';
import { APIRoutes } from '@/services/api.routes.ts';
import { PAGINATION } from '@/config';
import type { Symbol as SymbolModel, StockExchangeID, StockExchange } from '@/models';

export interface SymbolEntry {
  rawInput: string;
  parsedTicker: string;
  parsedExchange: string | null;
  existingSymbol: SymbolModel | null;
}

interface CreateMissingSymbolsModalProps {
  isOpen: boolean;
  onClose: () => void;
  symbols: SymbolEntry[];
  onSymbolsReady: (symbolMap: Record<string, SymbolModel>) => void;
}

interface SymbolRow {
  rawInput: string;
  parsedTicker: string;
  existingSymbol: SymbolModel | null;
  exchangeID: StockExchangeID | '';
  name: string;
  sector: string;
  status: 'existing' | 'verified' | 'unverified' | 'failed';
  verifying: boolean;
}

export function parseTickerInput(input: string): { ticker: string; exchange: string | null } {
  const trimmed = input.trim().toUpperCase();
  const colonIndex = trimmed.indexOf(':');
  if (colonIndex > 0 && colonIndex < trimmed.length - 1) {
    return { ticker: trimmed.substring(colonIndex + 1), exchange: trimmed.substring(0, colonIndex) };
  }
  return { ticker: trimmed, exchange: null };
}

export default function CreateMissingSymbolsModal({
  isOpen,
  onClose,
  symbols,
  onSymbolsReady
}: CreateMissingSymbolsModalProps) {
  const [rows, setRows] = useState<SymbolRow[]>([]);
  const [exchanges, setExchanges] = useState<StockExchange[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const verifyTimers = useRef<Record<string, NodeJS.Timeout>>({});

  // Fetch exchanges when modal opens
  useEffect(() => {
    if (isOpen) {
      APIRoutes.listStockExchanges({ limit: PAGINATION.FETCH_ALL_LIMIT }).then(response => {
        setExchanges(response.stockExchanges);
      }).catch((err) => {
        handleAPIError(err);
      });
    }
  }, [isOpen]);

  // Initialize rows when symbols change
  useEffect(() => {
    if (!isOpen || symbols.length === 0) return;

    const newRows: SymbolRow[] = symbols.map(entry => {
      if (entry.existingSymbol !== null) {
        return {
          rawInput: entry.rawInput,
          parsedTicker: entry.parsedTicker,
          existingSymbol: entry.existingSymbol,
          exchangeID: entry.existingSymbol.stockExchangeId,
          name: entry.existingSymbol.name ?? '',
          sector: entry.existingSymbol.sector ?? '',
          status: 'existing' as const,
          verifying: false
        };
      }

      // New symbol - try to match exchange from parsed input
      let matchedExchangeID: StockExchangeID | '' = '';
      if (entry.parsedExchange !== null && exchanges.length > 0) {
        const match = exchanges.find(e => e.identifier === entry.parsedExchange);
        if (match !== undefined) {
          matchedExchangeID = match.id;
        }
      }

      return {
        rawInput: entry.rawInput,
        parsedTicker: entry.parsedTicker,
        existingSymbol: null,
        exchangeID: matchedExchangeID,
        name: '',
        sector: '',
        status: matchedExchangeID !== '' ? 'unverified' as const : 'unverified' as const,
        verifying: false
      };
    });

    setRows(newRows);
    setError('');

    // Auto-verify rows that have a pre-selected exchange
    for (const row of newRows) {
      if (row.existingSymbol === null && row.exchangeID !== '') {
        verifyRow(row.parsedTicker, row.exchangeID, row.rawInput, newRows);
      }
    }
  }, [isOpen, symbols, exchanges]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      for (const timer of Object.values(verifyTimers.current)) {
        clearTimeout(timer);
      }
    };
  }, []);

  const verifyRow = async (ticker: string, exchangeID: StockExchangeID, rawInput: string, currentRows?: SymbolRow[]) => {
    const updateRows = (updater: (rows: SymbolRow[]) => SymbolRow[]) => {
      if (currentRows !== undefined) {
        currentRows = updater(currentRows);
      }
      setRows(updater);
    };

    updateRows(prev => prev.map(r =>
      r.rawInput === rawInput ? { ...r, verifying: true } : r
    ));

    try {
      const result = await APIRoutes.verifySymbol(ticker, exchangeID);
      updateRows(prev => prev.map(r =>
        r.rawInput === rawInput
          ? { ...r, verifying: false, status: result.found ? 'verified' : 'failed' }
          : r
      ));
    } catch {
      updateRows(prev => prev.map(r =>
        r.rawInput === rawInput ? { ...r, verifying: false, status: 'failed' } : r
      ));
    }
  };

  const handleExchangeChange = (rawInput: string, exchangeID: StockExchangeID | '') => {
    setRows(prev => prev.map(r =>
      r.rawInput === rawInput
        ? { ...r, exchangeID, status: exchangeID !== '' ? 'unverified' : 'unverified' }
        : r
    ));

    // Clear existing timer
    if (verifyTimers.current[rawInput] !== undefined) {
      clearTimeout(verifyTimers.current[rawInput]);
    }

    if (exchangeID !== '') {
      const row = rows.find(r => r.rawInput === rawInput);
      if (row !== undefined && row.existingSymbol === null) {
        verifyTimers.current[rawInput] = setTimeout(() => {
          verifyRow(row.parsedTicker, exchangeID, rawInput);
          delete verifyTimers.current[rawInput];
        }, 500);
      }
    }
  };

  const handleFieldChange = (rawInput: string, field: 'name' | 'sector', value: string) => {
    setRows(prev => prev.map(r =>
      r.rawInput === rawInput ? { ...r, [field]: value } : r
    ));
  };

  const allHaveExchange = rows.every(r => r.exchangeID !== '');

  const handleCreate = async () => {
    setError('');
    setLoading(true);

    try {
      const symbolMap: Record<string, SymbolModel> = {};

      for (const row of rows) {
        if (row.existingSymbol !== null) {
          // Check if name or sector changed (empty string in UI equals null in backend)
          const effectiveName = row.name !== '' ? row.name : null;
          const effectiveSector = row.sector !== '' ? row.sector : null;
          const nameChanged = effectiveName !== row.existingSymbol.name;
          const sectorChanged = effectiveSector !== row.existingSymbol.sector;

          if (nameChanged || sectorChanged) {
            const updated = await APIRoutes.updateSymbol(
              row.existingSymbol.id,
              row.existingSymbol.ticker,
              row.name,
              row.sector,
              row.existingSymbol.showInTicker,
              row.existingSymbol.stockExchangeId
            );
            symbolMap[row.rawInput] = updated;
          } else {
            symbolMap[row.rawInput] = row.existingSymbol;
          }
        } else {
          // Create new symbol
          if (row.exchangeID === '') {
            setError(`Bitte Börse für "${row.parsedTicker}" auswählen`);
            setLoading(false);
            return;
          }
          const created = await APIRoutes.createSymbol(
            row.parsedTicker,
            row.name,
            row.sector,
            false,
            row.exchangeID
          );
          symbolMap[row.rawInput] = created;
        }
      }

      // Also map any other rawInputs that resolve to the same parsedTicker
      for (const entry of symbols) {
        if (symbolMap[entry.rawInput] === undefined) {
          // Find by ticker match
          const matchingRow = rows.find(r => r.parsedTicker === entry.parsedTicker);
          if (matchingRow !== undefined && symbolMap[matchingRow.rawInput] !== undefined) {
            symbolMap[entry.rawInput] = symbolMap[matchingRow.rawInput];
          }
        }
      }

      onSymbolsReady(symbolMap);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Erstellen der Symbole';
      setError(errorMessage);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (row: SymbolRow) => {
    if (row.verifying) {
      return <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin text-gray-400" />;
    }

    switch (row.status) {
      case 'existing':
        return <FontAwesomeIcon icon={faCircleCheck} className="w-4 h-4 text-gray-500" title="Symbol existiert bereits" />;
      case 'verified':
        return <FontAwesomeIcon icon={faCircleCheck} className="w-4 h-4 text-green-500" title="Verifiziert" />;
      case 'failed':
        return <FontAwesomeIcon icon={faTriangleExclamation} className="w-4 h-4 text-amber-500" title="Verifizierung fehlgeschlagen" />;
      case 'unverified':
        if (row.exchangeID === '') {
          return <FontAwesomeIcon icon={faCircleXmark} className="w-4 h-4 text-red-500" title="Börse erforderlich" />;
        }
        return <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin text-gray-400" />;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Fehlende Symbole"
      icon={<FontAwesomeIcon icon={faCircleCheck} className="w-5 h-5 text-blue-600" />}
      maxWidth="4xl"
      closeDisabled={loading}
    >
      <div className="p-4 sm:p-6 space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Die folgenden Symbole existieren nicht in der Datenbank und müssen erstellt werden.
        </p>

        <MultiCreateTable
          rows={rows}
          keyExtractor={(row) => row.rawInput}
          fields={[
            {
              key: 'ticker',
              header: 'Ticker',
              primary: true,
              render: (row) => (
                <span className="text-sm font-mono text-gray-900 dark:text-white">
                  {row.parsedTicker}
                </span>
              )
            },
            {
              key: 'exchange',
              header: 'Börse *',
              render: (row) => (
                <select
                  value={row.exchangeID}
                  onChange={(e) => handleExchangeChange(row.rawInput, e.target.value as StockExchangeID)}
                  disabled={row.existingSymbol !== null || loading}
                  className={`w-full md:w-40 px-2 py-1 border rounded text-sm cursor-pointer ${
                    row.existingSymbol !== null
                      ? 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-500 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <option value="">-- Börse wählen --</option>
                  {exchanges.map(ex => (
                    <option key={ex.id} value={ex.id}>{ex.identifier} - {ex.name}</option>
                  ))}
                </select>
              )
            },
            {
              key: 'name',
              header: 'Name',
              render: (row) => (
                <input
                  type="text"
                  value={row.name}
                  onChange={(e) => handleFieldChange(row.rawInput, 'name', e.target.value)}
                  disabled={loading}
                  className="w-full md:w-40 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  placeholder="Optional"
                />
              )
            },
            {
              key: 'sector',
              header: 'Branche',
              render: (row) => (
                <input
                  type="text"
                  value={row.sector}
                  onChange={(e) => handleFieldChange(row.rawInput, 'sector', e.target.value)}
                  disabled={loading}
                  className="w-full md:w-40 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  placeholder="Optional"
                />
              )
            },
            {
              key: 'status',
              header: 'Status',
              align: 'center',
              mobileTrailing: true,
              render: (row) => getStatusIcon(row)
            }
          ]}
        />

        {error !== '' && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={loading || !allHaveExchange}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                Erstelle Symbole...
              </>
            ) : (
              'Symbole erstellen & fortfahren'
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
